"use client"

/**
 * How an import finishes on Add questions — review triage vs land as drafts.
 *
 * Set from the profile menu (same placement as search mode and the assemble /
 * adapt journey) so the import sheet is not spending its first viewport on a
 * standing preference. Read by the import sheet and job panels.
 */

import * as React from "react"

import {
  getStorageItem,
  setStorageItem,
  subscribeToStorageKey,
} from "@exxatdesignux/ui/lib/persisted-state"

export type ImportApproach = "review" | "direct"

export const IMPORT_APPROACHES: {
  id: ImportApproach
  label: string
  description: string
}[] = [
  {
    id: "review",
    label: "Review first",
    description:
      "Triage what came out, then add what you keep to the pool",
  },
  {
    id: "direct",
    label: "Import as draft",
    description:
      "Questions join Imported as drafts when the read finishes — no triage",
  },
]

const STORAGE_KEY = "import-approach"
/** Bumped when the default flipped to Review first. */
const VERSION = 3
const DEFAULT_APPROACH: ImportApproach = "review"

const listeners = new Set<() => void>()
let snapshot: ImportApproach | null = null
let hydrated = false

function subscribe(onChange: () => void) {
  listeners.add(onChange)
  const unsubscribeStorage = subscribeToStorageKey(STORAGE_KEY, () => {
    hydrated = false
    onChange()
  })
  return () => {
    listeners.delete(onChange)
    unsubscribeStorage()
  }
}

function isImportApproach(value: unknown): value is ImportApproach {
  return value === "review" || value === "direct"
}

function read(): ImportApproach {
  if (hydrated && snapshot) return snapshot
  hydrated = true

  const raw = getStorageItem(STORAGE_KEY)
  if (raw) {
    try {
      const parsed: unknown = JSON.parse(raw)
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        (parsed as { v?: number }).v === VERSION &&
        isImportApproach((parsed as { d?: unknown }).d)
      ) {
        snapshot = (parsed as { d: ImportApproach }).d
        return snapshot
      }
    } catch {
      // Unparseable payload — fall through to the default.
    }
  }

  snapshot = DEFAULT_APPROACH
  return snapshot
}

export function setImportApproach(next: ImportApproach) {
  snapshot = next
  hydrated = true
  setStorageItem(STORAGE_KEY, JSON.stringify({ v: VERSION, d: next }))
  for (const listener of listeners) listener()
}

/** Sync read for callers that must not close over a stale render (import start). */
export function getImportApproach(): ImportApproach {
  return read()
}

export function useImportApproach(): ImportApproach {
  return React.useSyncExternalStore(subscribe, read, () => DEFAULT_APPROACH)
}
